import React from "react";
import styled, { keyframes } from "styled-components";
import { AkChevronDownIcon } from "@fuegokit/fuegoicons-react";
import { themeGet, Text, Accordion } from "@fuegokit/react";

export const StyledAccordion = styled(Accordion)`
  &[data-orientation="horizontal"] {
    display: flex;
    max-width: 40em;
    height: 50vh;
  }
  &[data-orientation="vertical"] {
    max-width: 20em;
  }
  font-family: ${themeGet("fontFamilies.normal")};
`;
export const StyledAccordionItem = styled(Accordion.Item)`
  &[data-orientation="horizontal"] {
    display: flex;
    border-right: 1px solid white;
  }
  &[data-orientation="vertical"] {
    border-bottom: 1px solid white;
  }
`;
export const StyledAccordionHeader = styled(Accordion.Header)`
  &[data-orientation="horizontal"] {
    height: 100%;
  }
  margin: 0;
`;
export const StyledAccordionTrigger = styled(Accordion.Trigger)`
  // because it's a button, we want to stretch it
  &[data-orientation="horizontal"] {
    height: 100%;
  }
  &[data-orientation="vertical"] {
    width: 100%;
  }
  // and remove center text alignment in favour of inheriting
  text-align: inherit;
  box-sizing: border-box;
  appearance: none;
  border: none;
  padding: ${themeGet("space.2")};
  background-color: ${themeGet("colors.background.accent.gray.bolder")};
  color: ${themeGet("colors.text.inverse")};
  font-family: inherit;
  font-size: ${themeGet("fontSizes.4")};
  --shadow-color: ${themeGet("colors.background.accent.red.subtle")};

  &:focus {
    outline: none;
    box-shadow: inset 0 -5px 0 0 var(--shadow-color);
    color: ${themeGet("colors.text.accent.red.default")};
  }

  &[data-disabled] {
    color: ${themeGet("colors.text.disabled")};
  }

  &[data-state="open"] {
    background-color: ${themeGet("colors.background.accent.red.bolder")};
    color: ${themeGet("colors.text.inverse")};

    &:focus {
      --shadow-color: ${themeGet("colors.elevation.shadow.overlay")};
      color: ${themeGet("colors.text.default")};
    }
  }
`;
export const StyledAccordionContent = styled(Accordion.Content)`
  padding: ${themeGet("space.3")};
  line-height: ${themeGet("lineHeights.default")};
`;
const slideDown = keyframes`
  from {
    height: 0
  }
  to {
    height: var(--radix-accordion-content-height)
  }
`;
const slideUp = keyframes`
  from {
    height: var(--radix-accordion-content-height)
  }
  to {
    height: 0
  }
`;
const open2D = keyframes`
  from {
    width: 0;
    height: 0
  }
  to {
    width: 'var(--radix-accordion-content-width)';
    height: 'var(--radix-accordion-content-height)'
  }
`;
const close2D = keyframes`
  from {
    width: 'var(--radix-accordion-content-width)';
    height: 'var(--radix-accordion-content-height)'
  }
  to {
    width: 0;
    height: 0
  }
`;
export const StyledAnimatedAccordionContent = styled(Accordion.Content)`
  overflow: "hidden";
  &[data-state="open"] {
    animation: ${slideDown} 300ms ease-out;
  }
  &[data-state="closed"] {
    animation: ${slideUp} 300ms ease-out;
  }
`;
export const StyledAnimated2DAccordionContent = styled(Accordion.Content)`
  overflow: hidden;
  &[data-state="open"] {
    animation: ${open2D} 1000ms ease-out;
  }
  &[data-state="closed"] {
    animation: ${close2D} 1000ms ease-out;
  }
`;
export const StyledAccordionRootAttr = styled(Accordion)`
  background-color: rgba(0, 0, 255, 0.3);
  border: 2px solid blue;
  padding: ${themeGet("space.3")};

  &[data-state="closed"] {
    border-color: red;
  }
  &[data-state="open"] {
    border-color: green;
  }
  &[data-disabled] {
    border-style: dashed;
  }
  &:disabled {
    opacity: 0.5;
  }
`;
export const StyledAccordionItemAttr = styled(Accordion.Item)`
  background-color: rgba(0, 0, 255, 0.3);
  border: 2px solid blue;
  padding: ${themeGet("space.3")};

  &[data-state="closed"] {
    border-color: red;
  }
  &[data-state="open"] {
    border-color: green;
  }
  &[data-disabled] {
    border-style: dashed;
  }
  &:disabled {
    opacity: 0.5;
  }
`;
export const StyledAccordionHeaderAttr = styled(Accordion.Header)`
  background-color: rgba(0, 0, 255, 0.3);
  border: 2px solid blue;
  padding: ${themeGet("space.3")};

  &[data-state="closed"] {
    border-color: red;
  }
  &[data-state="open"] {
    border-color: green;
  }
  &[data-disabled] {
    border-style: dashed;
  }
  &:disabled {
    opacity: 0.5;
  }
`;
export const StyledAccordionTriggerAttr = styled(Accordion.Trigger)`
  background-color: rgba(0, 0, 255, 0.3);
  border: 2px solid blue;
  padding: ${themeGet("space.3")};

  &[data-state="closed"] {
    border-color: red;
  }
  &[data-state="open"] {
    border-color: green;
  }
  &[data-disabled] {
    border-style: dashed;
  }
  &:disabled {
    opacity: 0.5;
  }
`;
export const StyledAccordionContentAttr = styled(Accordion.Content)`
  display: "block";
  background-color: rgba(0, 0, 255, 0.3);
  border: 2px solid blue;
  padding: ${themeGet("space.3")};

  &[data-state="closed"] {
    border-color: red;
  }
  &[data-state="open"] {
    border-color: green;
  }
  &[data-disabled] {
    border-style: dashed;
  }
  &:disabled {
    opacity: 0.5;
  }
`;

export const StyledDemoAccordion = styled(Accordion)`
  border-radius: ${themeGet("radii.1")};
  width: 354px;
  background-color: ${themeGet("colors.elevation.surface.default.[default]")};
  box-shadow: ${themeGet("colors.elevation.shadow.overlay")};
`;
export const StyledDemoAccordionItem = styled(Accordion.Item)`
  overflow: hidden;
  background-color: ${themeGet("colors.elevation.surface.default.[default]")};

  &:first-child {
    margin-top: 0;
    border-top-left-radius: ${themeGet("radii.1")};
    border-top-right-radius: ${themeGet("radii.1")};
  }
  &:last-child {
    border-bottom-left-radius: ${themeGet("radii.1")};
    border-bottom-right-radius: ${themeGet("radii.1")};
  }
  &:focus-within {
    position: relative;
    z-index: 1;
    box-shadow: ${themeGet("colors.elevation.shadow.overlay")};
  }
`;
export const StyledDemoAccordionTrigger = styled(Accordion.Trigger)`
  font-family: inherit;
  background-color: transparent;
  padding: 0 ${themeGet("space.4")};
  height: ${themeGet("space.6")};
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  //
  font-size: ${themeGet("fontSizes.3")};
  line-height: 1;
  color: ${themeGet("colors.icon.default")};
  box-shadow: ${themeGet("shadows.elevation.shadow.raised")};
  background-color: ${themeGet("colors.elevation.surface.default.[default]")};
  border: 1px solid ${themeGet("colors.border.default")};
  &:hover {
    cursor: pointer;
    background-color: ${themeGet("colors.elevation.surface.default.hovered")};
  }
  svg {
    color: ${themeGet("colors.icon.default")};
    transition: transform 300ms cubic-bezier(0.87, 0, 0.13, 1);
  }
  &[data-state="open"] > svg {
    transform: rotate(180deg);
  }
`;
export const StyledDemoAccordionContent = styled(Accordion.Content)`
  --radix-collapsible-content-height: 75px;
  overflow: hidden;
  font-size: ${themeGet("fontSizes.3")};
  color: ${themeGet("colors.text.default")};
  background-color: ${themeGet("colors.background.neutral.default.[default]")};
  padding: ${themeGet("space.3")};
  &[data-state="open"] {
    animation: ${slideDown} 300ms cubic-bezier(0.87, 0, 0.13, 1);
  }
  &[data-state="closed"] {
    animation: ${slideUp} 300ms cubic-bezier(0.87, 0, 0.13, 1);
  }
`;
export const StyledDemoAccordionContentText = styled(Text)`
  /* padding: ${themeGet("space.3")} ${themeGet("space.4")}; */
`;
export const StyledDemoAccordionHeader = styled(Accordion.Header)`
  display: flex;
`;

type DemoAccordionComponentProps = {
  children?: React.ReactNode;
};
export const DemoAccordionTrigger = React.forwardRef(
  ({ children, ...props }: DemoAccordionComponentProps, forwardedRef) => (
    <StyledDemoAccordionHeader>
      <StyledDemoAccordionTrigger {...props} ref={forwardedRef}>
        {children}
        <AkChevronDownIcon aria-hidden />
      </StyledDemoAccordionTrigger>
    </StyledDemoAccordionHeader>
  )
);
DemoAccordionTrigger.displayName = "DemoAccordionTrigger";

export const DemoAccordionContent = React.forwardRef(
  ({ children, ...props }: DemoAccordionComponentProps, forwardedRef) => (
    <StyledDemoAccordionContent {...props} ref={forwardedRef}>
      <StyledDemoAccordionContentText>
        {children}
      </StyledDemoAccordionContentText>
    </StyledDemoAccordionContent>
  )
);
DemoAccordionContent.displayName = "DemoAccordionContent";
